/**
 * CameraView - Photo capture, upload, and AI analysis flow
 *
 * Extracted from App.js (Phase 1 refactor). Pure rendering + camera-local state.
 *
 * Required props (3 groups):
 *
 * cameraState = {
 *   cameraMode,           // 'camera' | 'upload' — set by App.js openCameraMode
 *   analyzing,            // shared loading state (used by manual entry, exercise, etc.)
 * }
 *
 * cameraActions = {
 *   setAnalyzing,         // (bool) => void — shared loading state setter
 *   onClose,              // () => void — navigate back to home, called after local cleanup
 *   onShowManualEntry,    // () => void
 *   onShowFoodContribution, // () => void
 *   onGuestMealSuccess,   // (mealData) => void — store guest meal locally
 *   loadTodayMeals,       // (userId) => Promise<updatedTotals>
 *   setEatingOutContext,   // (ctx|null) => void
 *   trackMealLogged,      // (source, data) => void
 *   showMiqNudge,         // (type) => void
 *   setFirstMealData,     // (data) => void
 *   setShowFirstMealInsight, // (bool) => void
 *   triggerPostMealComplete, // (totals, meal, force) => void
 *   fetchPostLogInsight,  // (meal, type) => Promise
 * }
 *
 * cameraConfig = {
 *   BACKEND_URL,          // API base URL
 *   userId,               // Current user ID (or null for guest)
 *   isGuestMode,          // boolean
 *   eatingOutContext,      // { restaurantName } | null
 *   totalMealsLogged,     // number — for first-meal detection
 *   pendingImage,         // base64 string | null — pre-selected image from guest flow
 *   clearPendingImage,    // () => void — clear pending image after use
 * }
 *
 * Lifecycle:
 *   1. App.js sets currentView='camera' + cameraMode='camera'|'upload'
 *   2. CameraView mounts, reads cameraMode to decide initial UI
 *   3. If cameraMode='camera', startCamera() runs on mount
 *   4. User captures/uploads photo → analyzeImage → confirmation screen
 *   5. User confirms → confirmMealAnalysis → calls cameraActions callbacks → onClose
 *   6. On cancel/close, local state resets and onClose is called
 */

import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Camera, Upload, RefreshCw, X, Check, Sparkles, Lightbulb, AlertTriangle, UtensilsCrossed, Globe } from 'lucide-react';
import debug from '../../utils/debug';
import { shouldForceFailure } from '../../config/appConfig';
import {
  shouldUseFileInputForCamera,
  handleCameraError,
  updateCameraState,
} from '../../utils/iosCompat';
import { trackMealLogged as trackMealLoggedUtil } from '../../utils/analytics';
import { brand } from '../../styles/brand';

const { colors, gradients, typography, radius, shadows } = brand;

// ═══════════════════════════════════════════════════════════════════════════════
// CameraView Component
// ═══════════════════════════════════════════════════════════════════════════════

const CameraView = ({ cameraState, cameraActions, cameraConfig }) => {
  const { cameraMode: initialCameraMode, analyzing } = cameraState;
  const {
    setAnalyzing,
    onClose,
    onShowManualEntry,
    onShowFoodContribution,
    onGuestMealSuccess,
    loadTodayMeals,
    setEatingOutContext,
    trackMealLogged,
    showMiqNudge,
    setFirstMealData,
    setShowFirstMealInsight,
    triggerPostMealComplete,
    fetchPostLogInsight,
  } = cameraActions;
  const {
    BACKEND_URL,
    userId,
    isGuestMode,
    eatingOutContext,
    totalMealsLogged,
    pendingImage,
    clearPendingImage,
  } = cameraConfig;

  // ═══════════════════════════════════════════════════════════════════════════
  // LOCAL STATE — camera-only, not shared with other views
  // ═══════════════════════════════════════════════════════════════════════════

  // Camera mode — initialized from prop, can change locally (e.g., camera→upload on error)
  const [mode, setMode] = useState(initialCameraMode);

  // Camera hardware
  const [stream, setStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Captured image
  const [capturedImage, setCapturedImage] = useState(null);

  // Analysis state (analyzing is shared — comes from props)
  const [preliminaryAnalysis, setPreliminaryAnalysis] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Confirmation form state
  const [userCorrections, setUserCorrections] = useState('');
  const [addedIngredients, setAddedIngredients] = useState('');
  const [selectedDish, setSelectedDish] = useState(null);
  const [selectedRicePortion, setSelectedRicePortion] = useState(null);
  const [customDishName, setCustomDishName] = useState('');
  const [macroOverrides, setMacroOverrides] = useState({
    calories: null,
    protein: null,
    carbs: null,
    fat: null,
  });
  const [justEditedMacro, setJustEditedMacro] = useState(null);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const iosCameraInputRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // SIDE EFFECTS — camera-only
  // ═══════════════════════════════════════════════════════════════════════════

  // Clear macro edit animation after delay
  useEffect(() => {
    if (justEditedMacro) {
      const timer = setTimeout(() => setJustEditedMacro(null), 600);
      return () => clearTimeout(timer);
    }
  }, [justEditedMacro]);

  // Start camera on mount if mode is 'camera'
  useEffect(() => {
    if (mode === 'camera' && !pendingImage) {
      const timer = setTimeout(() => startCamera(), 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line
  }, []);

  // Handle pre-selected image from guest flow
  useEffect(() => {
    if (pendingImage) {
      setCapturedImage(pendingImage);
      clearPendingImage();
      // Auto-trigger analysis after a tick so state is settled
      const timer = setTimeout(() => analyzeImage(pendingImage), 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CAMERA HANDLERS — hardware interaction, camera-only
  // ═══════════════════════════════════════════════════════════════════════════

  const startCamera = async () => {
    setCameraReady(false);
    setCameraError(null);

    // iOS-first approach: Use file input on iOS for reliability
    if (shouldUseFileInputForCamera()) {
      debug.log('[Camera] Using file input (iOS or fallback mode)');
      updateCameraState({ lastMethod: 'file_input', permissionStatus: 'n/a' });
      if (iosCameraInputRef.current) {
        iosCameraInputRef.current.click();
      }
      return;
    }

    try {
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        console.warn('[Camera] Not in secure context, camera may not work on mobile');
        updateCameraState({ lastError: { name: 'SecurityError', message: 'Not secure context' } });
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera is not supported in this browser. Please use the Upload Photo option.');
        updateCameraState({ lastError: { name: 'NotSupportedError', message: 'getUserMedia not available' } });
        return;
      }

      updateCameraState({ lastMethod: 'getUserMedia', permissionStatus: 'requesting' });

      let mediaStream;
      const videoConstraints = {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
      };

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
        updateCameraState({ permissionStatus: 'granted' });
      } catch (e) {
        debug.log('[Camera] Rear camera failed, trying fallback:', e.message);
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
          updateCameraState({ permissionStatus: 'granted' });
        } catch (fallbackError) {
          console.error('[Camera] All camera attempts failed:', fallbackError);
          const errorInfo = handleCameraError(fallbackError);
          updateCameraState({ lastError: errorInfo, permissionStatus: 'denied' });
          throw fallbackError;
        }
      }

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');

        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => {
              setTimeout(() => setCameraReady(true), 500);
            })
            .catch(playError => {
              console.error('[Camera] Video play failed:', playError);
              setCameraError('Could not start video preview. Please tap the screen to start the camera.');
            });
        };

        videoRef.current.onerror = (e) => {
          console.error('[Camera] Video element error:', e);
          setCameraError('Video playback error. Please use the Upload Photo option.');
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      const errorInfo = handleCameraError(error);
      updateCameraState({ lastError: errorInfo });
      setCameraError(errorInfo.userMessage);
    }
  };

  const handleIOSCameraCapture = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      debug.log('[Camera] iOS file capture received:', file.name);
      updateCameraState({ lastMethod: 'file_input', permissionStatus: 'granted' });
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
      };
      reader.onerror = (err) => {
        console.error('[Camera] File read error:', err);
        updateCameraState({ lastError: { name: 'FileReadError', message: err.message } });
        setCameraError('Failed to read image. Please try again.');
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraReady(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      alert('Camera not ready. Please wait a moment.');
      return;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      alert('Camera is loading. Please wait a moment and try again.');
      return;
    }
    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      if (imageData && imageData.length > 100) {
        setCapturedImage(imageData);
        stopCamera();
      } else {
        alert('Failed to capture photo. Please try again.');
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      alert('Failed to capture photo. Please try again.');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYSIS HANDLERS — bridge between camera-local and App-level state
  // ═══════════════════════════════════════════════════════════════════════════

  const analyzeImage = async (imageOverride) => {
    const imageToAnalyze = imageOverride || capturedImage;
    if (!imageToAnalyze) return;
    const analysisUserId = userId || 'guest_user';

    setAnalyzing(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 45000);

    try {
      if (imageToAnalyze.length < 1000) {
        clearTimeout(timeoutId);
        alert('Image appears to be invalid. Please try again.');
        setAnalyzing(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/meals/analyze/preliminary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: imageToAnalyze,
          user_id: analysisUserId,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        const hasUsefulEstimate = data.estimated_calories > 0 || data.estimated_protein > 0;
        const hasDishIdentification = data.initial_assessment &&
          !data.initial_assessment.includes('Unable to identify') &&
          !data.initial_assessment.includes('error in uploading') &&
          data.initial_assessment.length > 20;

        if (!hasUsefulEstimate && !hasDishIdentification) {
          const options = window.confirm('Could not identify food clearly. Would you like to:\n\nOK = Try Manual Entry\nCancel = Teach the app about this food');
          if (options) {
            setCapturedImage(null);
            onShowManualEntry();
          } else {
            setCapturedImage(null);
            onShowFoodContribution();
          }
          setAnalyzing(false);
          return;
        }

        setPreliminaryAnalysis(data);
        setMacroOverrides({ calories: null, protein: null, carbs: null, fat: null });
        setShowConfirmation(true);
        setAnalyzing(false);
      } else {
        alert('Failed to analyze image. Please try Manual Entry or take a clearer photo.');
        setAnalyzing(false);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.log('[Photo Analysis] Request timed out after 45 seconds');
        alert('Analysis is taking too long. The AI service may be busy. Please try again in a moment or use Manual Entry.');
        setAnalyzing(false);
        return;
      }
      console.error('Error analyzing image:', error);
      alert('Failed to analyze image. Please check your connection and try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const confirmMealAnalysis = async (useCorrections = false) => {
    const hasAddedIngredients = addedIngredients.trim().length > 0;
    const hasDishSelection = selectedDish && selectedDish !== preliminaryAnalysis?.primary_dish_guess;
    const hasRiceSelection = selectedRicePortion !== null;
    const hasCustomDish = customDishName.trim().length > 0;

    let enhancedCorrections = userCorrections || '';
    if (selectedDish && selectedDish !== 'other') {
      enhancedCorrections = `The dish is: ${selectedDish}. ${enhancedCorrections}`;
    } else if (hasCustomDish) {
      enhancedCorrections = `The dish is: ${customDishName}. ${enhancedCorrections}`;
    }
    if (hasRiceSelection) {
      const riceOption = preliminaryAnalysis?.rice_options?.find(r => r.value === selectedRicePortion);
      if (riceOption) {
        enhancedCorrections = `${enhancedCorrections} Rice portion: ${riceOption.label} (${riceOption.grams}g).`;
      }
    }

    if (!preliminaryAnalysis) return;

    // Determine final meal name
    let finalMealName = preliminaryAnalysis.initial_assessment;
    if (selectedDish && selectedDish !== 'other') {
      finalMealName = selectedDish;
      if (hasRiceSelection && selectedRicePortion > 0) {
        const riceOption = preliminaryAnalysis?.rice_options?.find(r => r.value === selectedRicePortion);
        finalMealName += ` with ${riceOption?.label || selectedRicePortion + ' cups'} rice`;
      }
    } else if (hasCustomDish) {
      finalMealName = customDishName;
      if (hasRiceSelection && selectedRicePortion > 0) {
        const riceOption = preliminaryAnalysis?.rice_options?.find(r => r.value === selectedRicePortion);
        finalMealName += ` with ${riceOption?.label || selectedRicePortion + ' cups'} rice`;
      }
    }

    // Guest user flow
    if (isGuestMode && !userId) {
      const mealData = {
        id: `guest_${Date.now()}`,
        food_items: finalMealName,
        calories: preliminaryAnalysis.estimated_calories,
        protein_g: preliminaryAnalysis.estimated_protein,
        carbs_g: preliminaryAnalysis.estimated_carbs,
        fat_g: preliminaryAnalysis.estimated_fat,
        timestamp: new Date().toISOString(),
      };
      onGuestMealSuccess(mealData);
      resetAllState();
      onClose();
      toast.success('Meal logged!');
      return;
    }

    if (!userId) return;

    setAnalyzing(true);
    try {
      let combinedCorrections = enhancedCorrections || '';
      if (addedIngredients.trim()) {
        const ingredientNote = `Added ingredients/toppings: ${addedIngredients.trim()}. Please factor these into the calorie and macro calculations.`;
        combinedCorrections = combinedCorrections
          ? `${combinedCorrections}. ${ingredientNote}`
          : ingredientNote;
      }

      // DEV-ONLY: Check for forced failure
      if (shouldForceFailure()) {
        toast.error('[DEV] Forced failure: Photo/Upload meal save blocked', { duration: 5000 });
        setAnalyzing(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/meals/analyze/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          image_base64: capturedImage,
          initial_assessment: finalMealName,
          user_corrections: combinedCorrections,
          added_ingredients: addedIngredients.trim(),
          selected_dish: selectedDish !== 'other' ? selectedDish : customDishName,
          selected_rice_portion: selectedRicePortion,
          // Only send macro overrides if user MANUALLY edited them (not the original AI estimate)
          ...(macroOverrides.calories != null && { estimated_calories: macroOverrides.calories }),
          ...(macroOverrides.protein != null && { estimated_protein: macroOverrides.protein }),
          ...(macroOverrides.carbs != null && { estimated_carbs: macroOverrides.carbs }),
          ...(macroOverrides.fat != null && { estimated_fat: macroOverrides.fat }),
          // Always send preliminary estimates separately for fallback use
          preliminary_calories: preliminaryAnalysis.estimated_calories || 0,
          preliminary_protein: preliminaryAnalysis.estimated_protein || 0,
          preliminary_carbs: preliminaryAnalysis.estimated_carbs || 0,
          preliminary_fat: preliminaryAnalysis.estimated_fat || 0,
          estimated_fiber: preliminaryAnalysis.estimated_fiber || 0,
          estimated_sugar: preliminaryAnalysis.estimated_sugar || 0,
          estimated_sodium: preliminaryAnalysis.estimated_sodium || 0,
          estimated_potassium: preliminaryAnalysis.estimated_potassium || 0,
          estimated_cholesterol: preliminaryAnalysis.estimated_cholesterol || 0,
          estimated_vitamin_a: preliminaryAnalysis.estimated_vitamin_a || 0,
          estimated_vitamin_c: preliminaryAnalysis.estimated_vitamin_c || 0,
          estimated_vitamin_d: preliminaryAnalysis.estimated_vitamin_d || 0,
          estimated_calcium: preliminaryAnalysis.estimated_calcium || 0,
          estimated_iron: preliminaryAnalysis.estimated_iron || 0,
          tz_offset: Math.round(-new Date().getTimezoneOffset() / 60),
          ...(eatingOutContext && {
            is_eating_out: true,
            restaurant_name: eatingOutContext.restaurantName,
          }),
        }),
      });

      // Clear eating-out context after meal is logged
      if (eatingOutContext) {
        setEatingOutContext(null);
      }

      if (response.ok) {
        const data = await response.json();
        const previousMealCount = totalMealsLogged;
        const updatedTotals = await loadTodayMeals(userId);

        // Reset all local state
        resetAllState();
        onClose();

        // Create meal data for insights
        const mealData = {
          food_items: data.food_items,
          calories: data.calories,
          protein: data.protein,
          carbs: data.carbs,
          fat: data.fat,
          fiber: data.fiber || 0,
          sodium: data.sodium || 0,
          medical_alerts: data.medical_alerts || [],
        };

        // Track meal logged event
        trackMealLogged(mode === 'camera' ? 'camera' : 'upload', {
          calories: data.calories,
          protein: data.protein,
          carbs: data.carbs,
          fat: data.fat,
        });

        // FIRST MEAL INSIGHT
        if (previousMealCount === 0) {
          setFirstMealData(mealData);
          setShowFirstMealInsight(true);
          showMiqNudge('first_meal');
          triggerPostMealComplete(updatedTotals, mealData, true);
        } else {
          const hour = new Date().getHours();
          let mealType = 'Other';
          if (hour >= 5 && hour < 11) mealType = 'Breakfast';
          else if (hour >= 11 && hour < 15) mealType = 'Lunch';
          else if (hour >= 15 && hour < 18) mealType = 'Snack';
          else if (hour >= 18 && hour < 22) mealType = 'Dinner';

          await fetchPostLogInsight(mealData, mealType);
          showMiqNudge('photo_log');
          triggerPostMealComplete(updatedTotals, mealData, true);
        }
      } else {
        alert('Failed to save meal. Please try again.');
      }
    } catch (error) {
      console.error('Error confirming meal:', error);
      alert('Failed to save meal. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPER — reset all local state
  // ═══════════════════════════════════════════════════════════════════════════

  const resetAllState = () => {
    stopCamera();
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setPreliminaryAnalysis(null);
    setShowConfirmation(false);
    setUserCorrections('');
    setAddedIngredients('');
    setSelectedDish(null);
    setSelectedRicePortion(null);
    setCustomDishName('');
    setMacroOverrides({ calories: null, protein: null, carbs: null, fat: null });
  };

  const handleClose = () => {
    resetAllState();
    onClose();
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="camera-view">
      {/* Hidden file input for iOS camera capture */}
      <input
        ref={iosCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleIOSCameraCapture}
        style={{ display: 'none' }}
        data-testid="ios-camera-input"
      />

      {/* AI Recognition Tips Disclaimer */}
      <div style={{
        background: colors.accentDim,
        border: `1px solid ${colors.accentBorder}`,
        borderRadius: radius.lg,
        padding: '14px 16px',
        marginBottom: '16px',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}>
        <p style={{
          fontSize: '13px',
          color: colors.textSecondary,
          margin: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          fontFamily: typography.fontFamily,
          lineHeight: '1.5',
        }}>
          <Lightbulb size={16} style={{ color: colors.accent, flexShrink: 0, marginTop: '1px' }} />
          <span>
            <strong style={{ color: colors.accent, fontFamily: typography.headingFont, fontWeight: '600' }}>For best AI recognition:</strong> Photograph each food item individually or ensure items are clearly separated on the plate. Mixed dishes may have less accurate nutritional estimates.
          </span>
        </p>
      </div>

      {!capturedImage ? (
        <div className="camera-container">
          {mode === 'camera' ? (
            <>
              {cameraError ? (
                /* Camera Error Fallback UI */
                <div style={{
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.25)',
                  borderRadius: radius.xl,
                  padding: '28px 24px',
                  textAlign: 'center',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'rgba(245, 158, 11, 0.12)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Camera size={26} style={{ color: colors.warning }} strokeWidth={1.5} />
                  </div>
                  <h3 style={{
                    color: colors.text,
                    margin: '0 0 8px 0',
                    fontSize: '18px',
                    fontFamily: typography.headingFont,
                    fontWeight: '500',
                    letterSpacing: '-0.01em',
                  }}>
                    Camera Unavailable
                  </h3>
                  <p style={{
                    color: colors.textSecondary,
                    margin: '0 0 16px 0',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    fontFamily: typography.fontFamily,
                  }}>
                    {cameraError}
                  </p>
                  <p style={{
                    color: colors.mutedText,
                    margin: '0 0 20px 0',
                    fontSize: '12px',
                    fontFamily: typography.fontFamily,
                  }}>
                    Tip: Upload a photo from your gallery — it works just as well.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button
                      className="btn-primary"
                      onClick={() => {
                        setCameraError(null);
                        setMode('upload');
                      }}
                      style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      data-testid="camera-unavailable-upload-btn"
                    >
                      <Upload size={16} />
                      Upload Photo Instead
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setCameraError(null);
                        startCamera();
                      }}
                      style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      data-testid="camera-retry-btn"
                    >
                      <RefreshCw size={16} />
                      Try Camera Again
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={handleClose}
                      style={{ width: '100%', opacity: 0.65 }}
                      data-testid="camera-unavailable-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="camera-wrapper">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      webkit-playsinline="true"
                      muted
                      className="video-preview"
                      style={{ display: 'block' }}
                    />
                    {!cameraReady && (
                      <div className="camera-loading">
                        <p>Starting camera...</p>
                      </div>
                    )}
                  </div>
                  <div className="camera-controls">
                    <button className="btn-secondary" onClick={handleClose} data-testid="camera-cancel-btn">Cancel</button>
                    <button
                      className="btn-primary"
                      onClick={capturePhoto}
                      disabled={!cameraReady}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      data-testid="camera-capture-btn"
                    >
                      <Camera size={16} />
                      {cameraReady ? 'Capture' : 'Loading...'}
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <Upload size={32} strokeWidth={1.25} style={{ color: colors.accent, opacity: 0.8 }} />
              <button
                className="btn-primary"
                onClick={() => fileInputRef.current?.click()}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                data-testid="camera-select-photo-btn"
              >
                <Upload size={16} />
                Select Photo
              </button>
              <button className="btn-secondary" onClick={handleClose} data-testid="camera-upload-cancel-btn">Cancel</button>
            </div>
          )}
        </div>
      ) : showConfirmation && preliminaryAnalysis ? (
        /* Interactive AI Analysis Confirmation */
        <ConfirmationPanel
          capturedImage={capturedImage}
          preliminaryAnalysis={preliminaryAnalysis}
          macroOverrides={macroOverrides}
          setMacroOverrides={setMacroOverrides}
          setJustEditedMacro={setJustEditedMacro}
          selectedDish={selectedDish}
          setSelectedDish={setSelectedDish}
          customDishName={customDishName}
          setCustomDishName={setCustomDishName}
          selectedRicePortion={selectedRicePortion}
          setSelectedRicePortion={setSelectedRicePortion}
          userCorrections={userCorrections}
          setUserCorrections={setUserCorrections}
          analyzing={analyzing}
          onDontLog={() => {
            setPreliminaryAnalysis(null);
            setShowConfirmation(false);
            setUserCorrections('');
            setAddedIngredients('');
            setSelectedDish(null);
            setSelectedRicePortion(null);
            setCustomDishName('');
            setCapturedImage(null);
          }}
          onConfirm={(useCorrections) => confirmMealAnalysis(useCorrections)}
        />
      ) : (
        <div className="preview-container">
          <img src={capturedImage} alt="Captured" className="captured-image" />
          <div className="preview-controls">
            <button
              className="btn-secondary"
              onClick={() => setCapturedImage(null)}
              disabled={analyzing}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              data-testid="camera-retake-btn"
            >
              <RefreshCw size={16} />
              Retake
            </button>
            <button
              className="btn-primary"
              onClick={() => analyzeImage()}
              disabled={analyzing}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              data-testid="camera-analyze-btn"
            >
              <Sparkles size={16} />
              {analyzing ? 'Analyzing...' : 'Analyze'}
            </button>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ConfirmationPanel — Sub-component for the AI analysis confirmation screen
// ═══════════════════════════════════════════════════════════════════════════════

const ConfirmationPanel = ({
  capturedImage,
  preliminaryAnalysis,
  macroOverrides,
  setMacroOverrides,
  setJustEditedMacro,
  selectedDish,
  setSelectedDish,
  customDishName,
  setCustomDishName,
  selectedRicePortion,
  setSelectedRicePortion,
  userCorrections,
  setUserCorrections,
  analyzing,
  onDontLog,
  onConfirm,
}) => {
  // Macro colors from brand tokens (protein=rose, carbs=gold, fat=violet)
  const MACRO_COLORS = {
    calories: colors.accent,
    protein: colors.protein,
    carbs: colors.carbs,
    fat: colors.fat,
  };
  return (
    <div className="preview-container" style={{ padding: '16px' }}>
      <img src={capturedImage} alt="Captured" className="captured-image" style={{ maxHeight: '200px', marginBottom: '16px', borderRadius: radius.lg, border: `1px solid ${colors.stroke}` }} />

      {/* AI's Initial Assessment — Premium Glass Card */}
      <div style={{
        background: colors.accentDim,
        border: `1px solid ${colors.accentBorder}`,
        borderRadius: radius.xl,
        padding: '18px',
        marginBottom: '16px',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: `0 4px 24px rgba(0,0,0,0.25), 0 0 40px ${colors.accentDim}`,
      }}>
        <p style={{
          fontSize: '11px',
          color: colors.accent,
          fontFamily: typography.headingFont,
          fontWeight: '600',
          marginBottom: '10px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Sparkles size={13} />
          AI Assessment{preliminaryAnalysis.cached ? ' · from memory' : ''}
        </p>
        <p style={{
          fontSize: '15px',
          color: colors.text,
          marginBottom: '10px',
          fontFamily: typography.headingFont,
          fontWeight: '400',
          letterSpacing: '-0.01em',
          lineHeight: '1.4',
        }}>
          {preliminaryAnalysis.message || preliminaryAnalysis.friendly_message || "Here's my initial analysis:"}
        </p>
        <p style={{
          fontSize: '13px',
          color: colors.textSecondary,
          marginBottom: '14px',
          fontFamily: typography.fontFamily,
          lineHeight: '1.5',
        }}>
          <strong style={{ color: colors.text, fontWeight: '600' }}>I see:</strong> {preliminaryAnalysis.initial_assessment}
        </p>

        {/* Pulse animation for macro editing */}
        <style>{`
          @keyframes macroPulse {
            0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(124, 92, 252, 0.4); }
            50% { transform: scale(1.02); box-shadow: 0 0 0 8px rgba(124, 92, 252, 0); }
            100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(124, 92, 252, 0); }
          }
        `}</style>

        {/* Editable Macros */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${colors.stroke}`,
          borderRadius: radius.lg,
          padding: '12px',
          marginBottom: '4px'
        }}>
          <p style={{
            fontSize: '10px',
            color: colors.mutedText,
            marginBottom: '10px',
            textAlign: 'center',
            fontFamily: typography.headingFont,
            fontWeight: '600',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            Tap any value to adjust
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <MacroInput label="Cal" color={MACRO_COLORS.calories} field="calories" unit="" prelimValue={preliminaryAnalysis.estimated_calories} macroOverrides={macroOverrides} setMacroOverrides={setMacroOverrides} setJustEditedMacro={setJustEditedMacro} />
            <MacroInput label="Pro" color={MACRO_COLORS.protein} field="protein" unit="g" prelimValue={preliminaryAnalysis.estimated_protein} macroOverrides={macroOverrides} setMacroOverrides={setMacroOverrides} setJustEditedMacro={setJustEditedMacro} />
            <MacroInput label="Carb" color={MACRO_COLORS.carbs} field="carbs" unit="g" prelimValue={preliminaryAnalysis.estimated_carbs} macroOverrides={macroOverrides} setMacroOverrides={setMacroOverrides} setJustEditedMacro={setJustEditedMacro} />
            <MacroInput label="Fat" color={MACRO_COLORS.fat} field="fat" unit="g" prelimValue={preliminaryAnalysis.estimated_fat} macroOverrides={macroOverrides} setMacroOverrides={setMacroOverrides} setJustEditedMacro={setJustEditedMacro} />
          </div>
          {(macroOverrides.calories !== null || macroOverrides.protein !== null || macroOverrides.carbs !== null || macroOverrides.fat !== null) && (
            <button
              onClick={() => setMacroOverrides({ calories: null, protein: null, carbs: null, fat: null })}
              style={{
                marginTop: '10px',
                padding: '5px 14px',
                fontSize: '10px',
                color: colors.mutedText,
                background: 'transparent',
                border: `1px solid ${colors.stroke}`,
                borderRadius: radius.pill,
                cursor: 'pointer',
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
                fontFamily: typography.headingFont,
                fontWeight: '500',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Reset to AI estimate
            </button>
          )}
        </div>
      </div>

      {/* Health Awareness Flags */}
      {preliminaryAnalysis.health_flags && preliminaryAnalysis.health_flags.length > 0 && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.06)',
          border: '1px solid rgba(245, 158, 11, 0.22)',
          borderRadius: radius.lg,
          padding: '14px 16px',
          marginBottom: '16px',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}>
          <p style={{
            fontSize: '11px',
            color: colors.warning,
            fontFamily: typography.headingFont,
            fontWeight: '600',
            marginBottom: '10px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <AlertTriangle size={13} />
            Health Awareness Notes
          </p>
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {preliminaryAnalysis.health_flags.map((flag, idx) => (
              <li key={idx} style={{
                fontSize: '13px',
                color: colors.textSecondary,
                marginBottom: '4px',
                fontFamily: typography.fontFamily,
                lineHeight: '1.5',
              }}>
                {flag}
              </li>
            ))}
          </ul>
          <p style={{
            fontSize: '11px',
            color: colors.mutedText,
            marginTop: '10px',
            fontStyle: 'italic',
            fontFamily: typography.fontFamily,
          }}>
            General awareness notes based on your health profile, not medical advice.
          </p>
        </div>
      )}

      {/* Enhanced Dish Clarification */}
      <DishClarificationPanel
        preliminaryAnalysis={preliminaryAnalysis}
        selectedDish={selectedDish}
        setSelectedDish={setSelectedDish}
        customDishName={customDishName}
        setCustomDishName={setCustomDishName}
        selectedRicePortion={selectedRicePortion}
        setSelectedRicePortion={setSelectedRicePortion}
        userCorrections={userCorrections}
        setUserCorrections={setUserCorrections}
      />

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          className="btn-secondary"
          onClick={onDontLog}
          disabled={analyzing}
          style={{
            background: 'rgba(239, 68, 68, 0.10)',
            border: '1px solid rgba(239, 68, 68, 0.32)',
            color: colors.error,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
          data-testid="confirm-dont-log-btn"
        >
          <X size={15} />
          Don&apos;t Log
        </button>
        <button
          className="btn-primary"
          onClick={() => onConfirm(false)}
          disabled={analyzing}
          style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          data-testid="confirm-save-btn"
        >
          <Check size={16} />
          {analyzing ? 'Saving...' : 'Looks Good, Save'}
        </button>
        {(userCorrections || selectedDish || selectedRicePortion !== null || customDishName) && (
          <button
            className="btn-primary"
            onClick={() => onConfirm(true)}
            disabled={analyzing}
            style={{
              flex: 1,
              background: gradients.primary,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
            data-testid="confirm-corrections-save-btn"
          >
            <RefreshCw size={15} />
            {analyzing
              ? 'Refining...'
              : `Confirm${selectedDish && selectedDish !== 'other' ? ` "${selectedDish.split(' ')[0]}..."` : customDishName ? ` "${customDishName.split(' ')[0]}..."` : ''} & Save`}
          </button>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MacroInput — Reusable editable macro field
// ═══════════════════════════════════════════════════════════════════════════════

const MacroInput = ({ label, color, field, unit, prelimValue, macroOverrides, setMacroOverrides, setJustEditedMacro }) => {
  const isOverridden = macroOverrides[field] !== null;
  const committedValue = macroOverrides[field] ?? prelimValue ?? 0;
  const [editValue, setEditValue] = useState(String(committedValue));
  const [isEditing, setIsEditing] = useState(false);

  // Sync display when parent value changes and user is NOT editing
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(macroOverrides[field] ?? prelimValue ?? 0));
    }
  }, [macroOverrides, field, prelimValue, isEditing]);

  return (
    <div
      data-testid={`photo-analysis-${field}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'rgba(255,255,255,0.04)',
        padding: '8px 10px',
        borderRadius: radius.md,
        border: isOverridden ? `1.5px solid ${color}` : `1px solid ${colors.stroke}`,
        transition: 'border-color 0.2s ease, background 0.2s ease',
        boxShadow: isOverridden ? `0 0 20px ${color}22` : 'none',
      }}
    >
      <span style={{
        fontSize: '10px',
        fontWeight: '700',
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        fontFamily: typography.headingFont,
        minWidth: '24px',
      }}>
        {label}
      </span>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        data-testid={`macro-input-${field}`}
        value={isEditing ? editValue : String(committedValue)}
        onFocus={(e) => {
          setIsEditing(true);
          setEditValue(String(committedValue));
          setTimeout(() => e.target.select(), 0);
          setTimeout(() => {
            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }}
        onChange={(e) => {
          const val = e.target.value.replace(/[^0-9]/g, '');
          setEditValue(val);
        }}
        onBlur={() => {
          const parsed = parseInt(editValue) || 0;
          setMacroOverrides(prev => ({ ...prev, [field]: parsed }));
          setJustEditedMacro(field);
          setIsEditing(false);
        }}
        style={{
          width: field === 'calories' ? '60px' : '50px',
          padding: '4px',
          border: 'none',
          background: 'transparent',
          color: isOverridden ? color : colors.text,
          fontSize: '18px',
          fontWeight: '600',
          fontFamily: typography.monoFont,
          letterSpacing: '-0.02em',
          textAlign: 'center',
          WebkitAppearance: 'none',
          MozAppearance: 'textfield',
          outline: 'none',
        }}
      />
      <span style={{
        fontSize: field === 'calories' ? '10px' : '11px',
        color: colors.mutedText,
        fontFamily: typography.fontFamily,
        fontWeight: '500',
      }}>
        {unit}{isOverridden && ' ✓'}
      </span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DishClarificationPanel — Dish selection, rice portion, and corrections
// ═══════════════════════════════════════════════════════════════════════════════

const DishClarificationPanel = ({
  preliminaryAnalysis,
  selectedDish,
  setSelectedDish,
  customDishName,
  setCustomDishName,
  selectedRicePortion,
  setSelectedRicePortion,
  userCorrections,
  setUserCorrections,
}) => {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${colors.stroke}`,
      borderRadius: radius.xl,
      padding: '16px',
      marginBottom: '16px',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
    }}>
      <p style={{
        fontSize: '11px',
        color: colors.accent,
        fontFamily: typography.headingFont,
        fontWeight: '600',
        marginBottom: '14px',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <UtensilsCrossed size={13} />
        Confirm the dish
      </p>

      {/* Dish Selection Chips */}
      {preliminaryAnalysis.dish_options && preliminaryAnalysis.dish_options.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <p style={{
            fontSize: '12px',
            color: colors.textSecondary,
            marginBottom: '10px',
            fontFamily: typography.fontFamily,
          }}>
            What dish is this?
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {preliminaryAnalysis.dish_options.map((dish, idx) => {
              const active = selectedDish === dish.value;
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedDish(dish.value);
                    if (dish.value !== 'other') setCustomDishName('');
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: radius.pill,
                    border: active ? `1.5px solid ${colors.accent}` : `1px solid ${colors.stroke}`,
                    background: active ? colors.accentDim : 'rgba(255,255,255,0.04)',
                    color: active ? colors.accent : colors.text,
                    fontSize: '13px',
                    fontWeight: dish.is_primary ? '600' : '500',
                    fontFamily: typography.fontFamily,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: active ? `0 0 20px ${colors.accentDim}` : 'none',
                  }}
                >
                  {dish.is_primary && '★ '}{dish.label}
                </button>
              );
            })}
          </div>
          {selectedDish === 'other' && (
            <input
              type="text"
              value={customDishName}
              onChange={(e) => setCustomDishName(e.target.value)}
              placeholder="Type the dish name (e.g., 'Butter Chicken')"
              style={{
                width: '100%',
                marginTop: '10px',
                padding: '11px 14px',
                borderRadius: radius.md,
                border: `1px solid ${colors.stroke}`,
                background: 'rgba(255,255,255,0.04)',
                color: colors.text,
                fontSize: '14px',
                fontFamily: typography.fontFamily,
                outline: 'none',
              }}
            />
          )}
        </div>
      )}

      {/* Rice Portion Chips */}
      {preliminaryAnalysis.rice_options && (
        <div style={{ marginBottom: '14px' }}>
          <p style={{
            fontSize: '12px',
            color: colors.textSecondary,
            marginBottom: '10px',
            fontFamily: typography.fontFamily,
          }}>
            Rice/grain portion
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {preliminaryAnalysis.rice_options.map((rice, idx) => {
              const active = selectedRicePortion === rice.value;
              const isDefault = preliminaryAnalysis.default_rice_cups === rice.value;
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedRicePortion(rice.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: radius.pill,
                    border: active ? `1.5px solid ${colors.carbs}` : `1px solid ${colors.stroke}`,
                    background: active ? 'rgba(234, 179, 8, 0.12)' : 'rgba(255,255,255,0.04)',
                    color: active ? colors.carbs : colors.text,
                    fontSize: '13px',
                    fontWeight: isDefault ? '600' : '500',
                    fontFamily: typography.fontFamily,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {isDefault && '• '}
                  {rice.label}
                  {rice.grams > 0 && (
                    <span style={{
                      fontSize: '10px',
                      opacity: 0.7,
                      marginLeft: '4px',
                      fontFamily: typography.monoFont,
                    }}>
                      ({rice.grams}g)
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Cuisine hint */}
      {preliminaryAnalysis.cuisine_region_guess && (
        <p style={{
          fontSize: '11px',
          color: colors.mutedText,
          marginBottom: '12px',
          fontFamily: typography.fontFamily,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <Globe size={12} />
          Detected cuisine: {preliminaryAnalysis.cuisine_region_guess}
          {preliminaryAnalysis.confidence && ` · ${preliminaryAnalysis.confidence} confidence`}
        </p>
      )}

      {/* Optional details textarea */}
      <textarea
        value={userCorrections}
        onChange={(e) => setUserCorrections(e.target.value)}
        placeholder="Any additional details? (e.g., 'extra creamy sauce', 'less oil than usual')"
        style={{
          width: '100%',
          minHeight: '64px',
          padding: '12px',
          borderRadius: radius.md,
          border: `1px solid ${colors.stroke}`,
          background: 'rgba(255,255,255,0.04)',
          color: colors.text,
          fontSize: '14px',
          fontFamily: typography.fontFamily,
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
};

export default CameraView;
